import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { AlertTriangle, X } from 'lucide-react'
import { supabase } from '../lib/supabase'
import { useBrand } from '../hooks/useBrand'
import { onAppRevalidate } from '../lib/revalidate'

// The refresh is a background worker job (see refreshCampaignProductCache in
// worker/src/hiring.ts), not anything this tab triggers. A Realtime
// subscription (below) picks up the change the moment the worker writes it —
// appearing and disappearing live, no reload needed. The poll here is just a
// safety net in case a realtime event is ever missed (dropped connection,
// reconnect gap).
//
// This banner is mounted on EVERY authenticated page, so its interval is the
// app's only permanent background timer: at the previous 60s it was a query per
// minute per open tab, forever, for a banner that is empty almost always and
// already has realtime on the exact rows it reads. Five minutes plus a refresh
// on tab return covers the dropped-connection case at a twelfth of the traffic.
const POLL_MS = 5 * 60_000

interface ErroringCampaign {
  id: string
  name: string
  product_id_error: string
}

/**
 * Persistent, page-agnostic banner — mounted once in App.tsx so it shows up
 * regardless of which page the brand is on. Surfaces campaigns where the
 * worker's background product-detail refresh couldn't resolve product_id
 * (invalid ID, delisted product, or an Insights API failure). Appears and
 * clears live via Realtime as soon as the worker's background job writes
 * product_id_error — no page refresh needed.
 */
export default function ProductIdErrorBanner() {
  const { brand } = useBrand()
  const [erroring, setErroring] = useState<ErroringCampaign[]>([])
  // In-memory only — a dismissed banner reappears on page refresh (component
  // remount), which is the intended behavior, not a bug to persist around.
  const [dismissedIds, setDismissedIds] = useState<Set<string>>(new Set())

  const load = useCallback(async () => {
    if (!brand?.id) {
      setErroring([])
      return
    }
    const { data, error } = await supabase
      .from('cr_campaigns')
      .select('id, name, product_id_error')
      .eq('brand_id', brand.id)
      .not('product_id_error', 'is', null)
    if (!error) setErroring((data as ErroringCampaign[]) ?? [])
  }, [brand?.id])

  useEffect(() => {
    load()
    const interval = setInterval(load, POLL_MS)
    // Also re-check when the user comes back to the tab — that is when a missed
    // realtime event is most likely and when the answer actually matters.
    const offFocus = onAppRevalidate(load, POLL_MS)

    if (!brand?.id) return () => { clearInterval(interval); offFocus() }

    // Instant appear/disappear — the worker's background refresh updates
    // product_id_error on this same row, so a plain UPDATE subscription
    // (no column filter) catches both the set and the clear.
    const channel = supabase
      .channel(`product_id_error_${brand.id}`)
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'cr_campaigns', filter: `brand_id=eq.${brand.id}` },
        load,
      )
      .subscribe()

    return () => {
      clearInterval(interval)
      offFocus()
      supabase.removeChannel(channel)
    }
  }, [load, brand?.id])

  const visible = erroring.filter(c => !dismissedIds.has(c.id))
  if (!visible.length) return null

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 24 }}>
      {visible.map(c => (
        <div
          key={c.id}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            background: 'rgba(239,68,68,0.08)',
            boxShadow: 'inset 0 0 0 1px rgba(239,68,68,0.25)',
            borderRadius: 10,
            padding: '11px 14px',
            fontSize: 13,
          }}
        >
          <AlertTriangle size={16} color="var(--danger, #ef4444)" style={{ flexShrink: 0 }} />
          <span style={{ flex: 1, color: 'var(--text-primary)' }}>
            You entered an invalid product ID for campaign <strong>"{c.name}"</strong> — please add a valid one.
          </span>
          <Link
            to={`/campaigns/new?edit=${c.id}`}
            style={{ color: 'var(--danger, #ef4444)', fontWeight: 600, textDecoration: 'none', whiteSpace: 'nowrap' }}
          >
            Fix now →
          </Link>
          <button
            type="button"
            onClick={() => setDismissedIds(prev => new Set(prev).add(c.id))}
            aria-label="Dismiss"
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: 'transparent', border: 'none', cursor: 'pointer',
              color: 'var(--text-secondary)', padding: 2, flexShrink: 0,
            }}
          >
            <X size={15} />
          </button>
        </div>
      ))}
    </div>
  )
}
